import { forwardRef } from 'react';
import { Button, Flex, InputRef } from 'antd';
import { TbSlash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CloudStorageConnection } from '@/services';
import { CloudStorageConnectionTrigger, SelectDataSourceDropdown } from '@/shared/components/dropdowns';
import { SearchFilterInput } from '@/shared/components/inputs';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { WorkspaceFolderGlyph } from '@/shared/components/workspace-resources';
import { SetStateMethod } from '@/types';

interface AttachmentPanelToolbarProps {
    canCreateFolder: boolean;
    connection: Partial<CloudStorageConnection>;
    filesAvailable: boolean;
    onCreateFolder: () => void;
    query: string;
    setConnection: SetStateMethod<Partial<CloudStorageConnection>>;
    setQuery: (query: string) => void;
}

const AttachmentPanelToolbar = forwardRef<InputRef, AttachmentPanelToolbarProps>(
    ({ canCreateFolder, connection, filesAvailable, onCreateFolder, query, setConnection, setQuery }, ref) => {
        const { token } = useAntDesignTheme();

        return (
            <Flex
                align='center'
                gap='0.5rem'
                style={{ borderBottom: `1px solid ${token.colorBorder}`, flex: 'none', padding: '0.75rem 1rem' }}
                wrap='wrap'
            >
                <div style={{ flex: '1 1 14rem', minWidth: 0 }}>
                    <SearchFilterInput
                        ref={ref}
                        disabled={!connection.id || !filesAvailable}
                        placeholder='Filter Attachments...'
                        setValue={setQuery}
                        value={query}
                        suffix={
                            <SmallTooltip title='Press / to focus Attachments search'>
                                <span
                                    aria-label='Press slash to focus Attachments search'
                                    role='img'
                                    style={{ alignItems: 'center', display: 'inline-flex', height: '100%' }}
                                >
                                    <TbSlash
                                        aria-hidden
                                        size='1rem'
                                        style={{
                                            color: token.colorTextQuaternary,
                                            display: 'block',
                                            transform: 'translateY(0.125rem)',
                                        }}
                                    />
                                </span>
                            </SmallTooltip>
                        }
                    />
                </div>
                <SelectDataSourceDropdown selectedConnection={connection} setSelectedConnection={setConnection}>
                    <CloudStorageConnectionTrigger selectedConnection={connection} />
                </SelectDataSourceDropdown>
                <Button
                    disabled={!canCreateFolder || !connection.id}
                    icon={<WorkspaceFolderGlyph variant='create' />}
                    onClick={onCreateFolder}
                >
                    Create Folder
                </Button>
            </Flex>
        );
    },
);

AttachmentPanelToolbar.displayName = 'AttachmentPanelToolbar';

export default AttachmentPanelToolbar;
